<template>
  <div ref="container" class="nebula-bg"></div>
</template>

<script>
/**
 * NebulaBackground — Three.js 全屏底层星云背景（深空望远镜质感）
 *
 * 多层缠绕蓝紫气态星云：大尺寸软光斑粒子 + 加色叠加，形成弥散云雾
 * ShaderMaterial 径向衰减消除方块感，亮星带十字星芒
 * 三层运动：云团缓慢自转缠绕、云团整体漂移、相机低频漂浮
 * slowTime = uTime * 0.15 统一低频，慢节奏、无抖动、有呼吸感
 * 低分辨率渲染（pixelRatio 上限 1.25）获得柔焦望远镜观感
 *
 * 注意：Three.js 对象不能放进 data()，否则被 Vue 3 包成响应式 Proxy，
 * 触发 modelViewMatrix 只读属性代理错误。全部挂 this.threeRefs。
 */
import * as THREE from 'three'

/* ── 顶点着色器：粒子级缓慢漂移 + 呼吸缩放 ── */
const VERT = `
attribute float aSize;
attribute float aSeed;
attribute vec3 aColor;
attribute float aAlpha;
attribute float aSpike;
uniform float uTime;
uniform float uDpr;
varying vec3 vColor;
varying float vAlpha;
varying float vSpike;
void main() {
  vec3 p = position;
  // 统一低频时间，粒子各自相位，产生云雾内部缓慢流动
  float slow = uTime * 0.15;
  p.x += sin(slow + aSeed * 6.2831) * 6.0;
  p.y += cos(slow * 0.8 + aSeed * 8.1662) * 5.0;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  // 呼吸：尺寸缓慢起伏
  float breathe = 1.0 + 0.14 * sin(uTime * 0.25 + aSeed * 12.566);
  gl_PointSize = aSize * breathe * uDpr;
  vColor = aColor;
  // 亮度极慢明暗变化（不闪烁）
  vAlpha = aAlpha * (0.82 + 0.18 * sin(uTime * 0.3 + aSeed * 9.0));
  vSpike = aSpike;
  gl_Position = projectionMatrix * mv;
}
`

/* ── 片元着色器：径向软光斑 + 亮星十字星芒 ── */
const FRAG = `
varying vec3 vColor;
varying float vAlpha;
varying float vSpike;
void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float d = length(uv);
  if (d > 0.5) discard;
  // 核心到边缘的柔和长尾衰减（大粒子呈现弥散气体而非颗粒）
  float core = smoothstep(0.5, 0.0, d);
  float glow = pow(core, 2.4);
  // 十字星芒：仅亮星启用
  float spike = 0.0;
  if (vSpike > 0.5) {
    float h = smoothstep(0.018, 0.0, abs(uv.y)) * smoothstep(0.5, 0.0, abs(uv.x));
    float v = smoothstep(0.018, 0.0, abs(uv.x)) * smoothstep(0.5, 0.0, abs(uv.y));
    spike = (h + v) * 0.45;
  }
  float a = clamp(glow + spike, 0.0, 1.0) * vAlpha;
  gl_FragColor = vec4(vColor, a);
}
`

/* Box-Muller 高斯随机：云核周边粒子按正态散布，形成团块而非均匀球 */
function gaussian () {
  let u = 0
  let v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

// 星云云团配置（韦伯式：蓝紫为主，点缀青/品红，多云核团块结构）
const NEBULA_CONFIGS = [
  { cx: -220, cy: 90, rx: 380, ry: 250, cores: 5, perCore: 130, sizeMin: 60, sizeMax: 130, alpha: 0.050, colors: [0x4477ff, 0x6688ff, 0x8866ff] },
  { cx: 230, cy: -70, rx: 340, ry: 230, cores: 4, perCore: 130, sizeMin: 60, sizeMax: 120, alpha: 0.050, colors: [0x8855ff, 0xaa66dd, 0x5544aa] },
  { cx: 60, cy: 130, rx: 300, ry: 200, cores: 4, perCore: 110, sizeMin: 70, sizeMax: 140, alpha: 0.042, colors: [0x3355aa, 0x4466bb, 0x66aaff] },
  { cx: -130, cy: -150, rx: 260, ry: 170, cores: 3, perCore: 100, sizeMin: 50, sizeMax: 105, alpha: 0.036, colors: [0x66ccff, 0x55bbee, 0x88ddff] },
  { cx: 190, cy: 150, rx: 220, ry: 155, cores: 3, perCore: 95, sizeMin: 50, sizeMax: 100, alpha: 0.034, colors: [0xcc66ff, 0xdd88ff, 0xaa55cc] }
]

// 星尘数量
const STAR_COUNT = 900

export default {
  name: 'NebulaBackground',
  data () {
    return {
      rafId: null,
      resizeHandler: null
    }
  },
  mounted () {
    // 非响应式 Three 对象容器
    this.threeRefs = {
      renderer: null,
      scene: null,
      camera: null,
      clock: null,
      uTime: 0,
      uDpr: 1,
      nebulaGroups: [],
      starField: null
    }
    this.init()
    this.animate()
  },
  beforeUnmount () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
    const T = this.threeRefs
    if (!T) return
    T.nebulaGroups.forEach(g => {
      T.scene.remove(g)
      g.geometry.dispose()
      g.material.dispose()
    })
    if (T.starField) {
      T.scene.remove(T.starField)
      T.starField.geometry.dispose()
      T.starField.material.dispose()
    }
    if (T.renderer) {
      T.renderer.dispose()
      if (T.renderer.domElement && this.$refs.container) {
        this.$refs.container.removeChild(T.renderer.domElement)
      }
    }
    this.threeRefs = null
  },
  methods: {
    // 创建统一的软光斑 ShaderMaterial
    makeGlowMaterial () {
      const T = this.threeRefs
      return new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uDpr: { value: T.uDpr }
        },
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      })
    },
    // 按云核 + 高斯散布生成一团星云粒子
    buildNebulaGroup (cfg, idx) {
      const count = cfg.cores * cfg.perCore
      const positions = new Float32Array(count * 3)
      const sizes = new Float32Array(count)
      const seeds = new Float32Array(count)
      const alphas = new Float32Array(count)
      const spikes = new Float32Array(count)
      const colors = new Float32Array(count * 3)
      const tmp = new THREE.Color()

      for (let c = 0; c < cfg.cores; c++) {
        // 每个云核在云团范围内随机定位
        const coreX = cfg.cx + gaussian() * cfg.rx * 0.35
        const coreY = cfg.cy + gaussian() * cfg.ry * 0.35
        for (let i = 0; i < cfg.perCore; i++) {
          const k = c * cfg.perCore + i
          // 云核周边高斯散布（扁平化模拟星云盘）
          positions[k * 3] = coreX + gaussian() * cfg.rx * 0.28
          positions[k * 3 + 1] = coreY + gaussian() * cfg.ry * 0.28
          positions[k * 3 + 2] = gaussian() * 60
          sizes[k] = cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin)
          seeds[k] = Math.random()
          alphas[k] = cfg.alpha * (0.5 + Math.random() * 0.5)
          spikes[k] = 0
          // 组内颜色随机微混
          tmp.setHex(cfg.colors[Math.floor(Math.random() * cfg.colors.length)])
          colors[k * 3] = tmp.r
          colors[k * 3 + 1] = tmp.g
          colors[k * 3 + 2] = tmp.b
        }
      }

      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
      geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1))
      geo.setAttribute('aAlpha', new THREE.BufferAttribute(alphas, 1))
      geo.setAttribute('aSpike', new THREE.BufferAttribute(spikes, 1))
      geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3))

      const points = new THREE.Points(geo, this.makeGlowMaterial())
      // 相邻云团反向自转，形成多层缠绕；肉眼可察觉但仍缓慢
      const dir = idx % 2 === 0 ? 1 : -1
      points.userData = {
        rotSpeed: dir * 0.00018 * (0.7 + idx * 0.15),
        driftPhase: Math.random() * Math.PI * 2,
        driftAmp: 22 + Math.random() * 16
      }
      return points
    },
    // 创建细密星尘（少量亮星带十字星芒）
    buildStarField () {
      const w = window.innerWidth
      const h = window.innerHeight
      const positions = new Float32Array(STAR_COUNT * 3)
      const sizes = new Float32Array(STAR_COUNT)
      const seeds = new Float32Array(STAR_COUNT)
      const alphas = new Float32Array(STAR_COUNT)
      const spikes = new Float32Array(STAR_COUNT)
      const colors = new Float32Array(STAR_COUNT * 3)
      const tmp = new THREE.Color()

      for (let i = 0; i < STAR_COUNT; i++) {
        positions[i * 3] = (Math.random() - 0.5) * w * 1.6
        positions[i * 3 + 1] = (Math.random() - 0.5) * h * 1.6
        positions[i * 3 + 2] = (Math.random() - 0.5) * 200
        // 大部分细星，少数大亮星
        const bright = Math.random() < 0.06
        sizes[i] = bright ? 5 + Math.random() * 5 : 1.6 + Math.random() * 2.6
        seeds[i] = Math.random()
        alphas[i] = bright ? 0.7 + Math.random() * 0.3 : 0.35 + Math.random() * 0.4
        spikes[i] = bright ? 1 : 0
        // 星色偏冷白蓝，偶现暖星
        tmp.setHex(Math.random() < 0.12 ? 0xffe8cc : 0xdde8ff)
        colors[i * 3] = tmp.r
        colors[i * 3 + 1] = tmp.g
        colors[i * 3 + 2] = tmp.b
      }

      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
      geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1))
      geo.setAttribute('aAlpha', new THREE.BufferAttribute(alphas, 1))
      geo.setAttribute('aSpike', new THREE.BufferAttribute(spikes, 1))
      geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3))
      return new THREE.Points(geo, this.makeGlowMaterial())
    },
    init () {
      const T = this.threeRefs
      const container = this.$refs.container
      const w = window.innerWidth
      const h = window.innerHeight

      T.scene = new THREE.Scene()
      T.camera = new THREE.OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, 1, 2000)
      T.camera.position.z = 800
      // 相机基准位置（漂浮时在其基础上叠加小幅偏移）
      T.camera.userData.baseX = 0
      T.camera.userData.baseY = 0

      // 限制渲染分辨率上限：柔焦望远镜质感 + 省 GPU
      T.uDpr = Math.min(window.devicePixelRatio || 1, 1.25)
      T.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false })
      T.renderer.setSize(w, h)
      T.renderer.setPixelRatio(T.uDpr)
      container.appendChild(T.renderer.domElement)

      T.clock = new THREE.Clock()

      // 多层气态星云
      T.nebulaGroups = NEBULA_CONFIGS.map((cfg, idx) => {
        const g = this.buildNebulaGroup(cfg, idx)
        T.scene.add(g)
        return g
      })

      // 星尘
      T.starField = this.buildStarField()
      T.scene.add(T.starField)

      this.resizeHandler = () => {
        const nw = window.innerWidth
        const nh = window.innerHeight
        T.camera.left = -nw / 2
        T.camera.right = nw / 2
        T.camera.top = nh / 2
        T.camera.bottom = -nh / 2
        T.camera.updateProjectionMatrix()
        T.renderer.setSize(nw, nh)
      }
      window.addEventListener('resize', this.resizeHandler)
    },
    animate () {
      this.rafId = requestAnimationFrame(this.animate)
      const T = this.threeRefs
      if (!T || !T.renderer) return

      const delta = Math.min(T.clock.getDelta(), 0.05)
      T.uTime += delta
      const t = T.uTime

      // 云团：缓慢自转缠绕 + 整体漂移
      T.nebulaGroups.forEach((g) => {
        g.rotation.z += g.userData.rotSpeed
        g.position.x = Math.sin(t * 0.03 + g.userData.driftPhase) * g.userData.driftAmp
        g.position.y = Math.cos(t * 0.024 + g.userData.driftPhase) * g.userData.driftAmp * 0.7
        g.material.uniforms.uTime.value = t
      })

      // 星尘：极慢逆向旋转 + shader 时间推进
      if (T.starField) {
        T.starField.rotation.z += 0.00004
        T.starField.material.uniforms.uTime.value = t
      }

      // 相机低频漂浮：基准位置叠加小幅偏移，z 不变
      T.camera.position.x = T.camera.userData.baseX + Math.sin(t * 0.02) * 12
      T.camera.position.y = T.camera.userData.baseY + Math.cos(t * 0.017) * 9

      T.renderer.render(T.scene, T.camera)
    }
  }
}
</script>

<style scoped>
.nebula-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}

.nebula-bg canvas {
  display: block;
}

/* 暗角：加深四周，强化深空望远镜构图（仍在 z-index:-1 层内，不影响内容） */
.nebula-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 52%, rgba(4, 6, 18, 0.55) 100%);
  pointer-events: none;
}
</style>
